import { useEffect, useMemo, useState } from "react";
import type { DayviseClient } from "@dayvise/client-sdk";

import { useActiveWorkspaceId, useDayviseClient } from "./providers.tsx";

export interface DeviceIdentityStore {
  read(): string | null;
  write(value: string): void;
}

export function memoryDeviceIdentityStore(): DeviceIdentityStore {
  let value: string | null = null;
  return { read: () => value, write: (next) => { value = next; } };
}

export function browserDeviceIdentityStore(key = "dayvise.deviceToken"): DeviceIdentityStore {
  return {
    read: () => (typeof localStorage === "undefined" ? null : localStorage.getItem(key)),
    write: (value) => { if (typeof localStorage !== "undefined") localStorage.setItem(key, value); },
  };
}

function newDeviceToken(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export async function ensureDeviceId(
  client: Pick<DayviseClient, "devices">,
  platform: "web" | "desktop" | "android" | "ios",
  store: DeviceIdentityStore,
): Promise<string> {
  const known = store.read();
  // Persist installation identity, never a workspace/user-scoped server ID.
  // Re-registration resolves the current account and honors device revocation.
  const installationId = known && /^[a-f0-9]{32}$/.test(known) ? known : newDeviceToken();
  store.write(installationId);
  const registered = await client.devices.register({ platform, installationId });
  return registered.device.deviceId;
}

/**
 * The device id this install is registered under, or `null` until it is known.
 *
 * The same persisted identity the presence reporter uses, so a native device grant
 * and a presence lease cannot name two different devices for one install. A failure
 * resolves to `null` rather than throwing: a screen that needs the id explains that
 * signing in is required instead of crashing.
 */
export function useDeviceId(platform: "web" | "desktop", userId: string | null): string | null {
  const client = useDayviseClient();
  const workspaceId = useActiveWorkspaceId();
  const store = useMemo(() => browserDeviceIdentityStore(`dayvise.deviceToken.${platform}`), [platform]);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  useEffect(() => {
    setDeviceId(null);
    if (userId === null || workspaceId === null) {
      setDeviceId(null);
      return;
    }
    let cancelled = false;
    void ensureDeviceId(client, platform, store)
      .then((resolved) => { if (!cancelled) setDeviceId(resolved); })
      .catch(() => { if (!cancelled) setDeviceId(null); });
    return () => { cancelled = true; };
  }, [client, platform, store, userId, workspaceId]);
  return deviceId;
}
